import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { validateEmailInput, showFieldError } from '@theme/utilities';

/** @typedef {object} BackInStockModalRefs
 * @property {HTMLFormElement} bisForm
 * @property {HTMLInputElement} emailInput
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} successMessage
 */

class BackInStockModal extends DialogComponent {
  /** @type {boolean} */
  isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();
    this.initValidation();
    this.addEventListener(DialogCloseEvent.eventName, this.#resetViewOnClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener(DialogCloseEvent.eventName, this.#resetViewOnClose);
    const emailInput = /** @type {HTMLInputElement | undefined} */ (this.refs?.emailInput);
    if (emailInput) {
      emailInput.removeEventListener('blur', this.#handleEmailBlur);
      emailInput.removeEventListener('input', this.#handleEmailInput);
    }
  }

  #resetViewOnClose = () => {
    const formContent = this.querySelector('.js-bis-form-content');
    const successMessage = this.refs?.successMessage;
    if (formContent) formContent.classList.remove('hidden');
    const el = Array.isArray(successMessage) ? successMessage[0] : successMessage;
    if (el instanceof HTMLElement) el.classList.add('hidden');

    const form = /** @type {HTMLFormElement | undefined} */ (this.refs?.bisForm);
    const emailInput = /** @type {HTMLInputElement | undefined} */ (this.refs?.emailInput);
    const errorElement = /** @type {HTMLElement | undefined} */ (this.refs?.errorMessage);
    if (form) form.reset();
    if (emailInput && errorElement) showFieldError(emailInput, errorElement, false);
    if (errorElement) errorElement.textContent = '';
  };

  /** @param {boolean} requireErrorOnEmpty */
  validateEmail(requireErrorOnEmpty) {
    const emailInput = /** @type {HTMLInputElement | undefined} */ (this.refs.emailInput);
    const errorElement = /** @type {HTMLElement | undefined} */ (this.refs.errorMessage);
    if (!emailInput || !errorElement) return false;
    return validateEmailInput(
      emailInput,
      errorElement,
      requireErrorOnEmpty,
      this.dataset.emailError || 'Submit a valid email.'
    );
  }

  #handleEmailBlur = () => {
    this.validateEmail(false);
  };

  #handleEmailInput = () => {
    const emailInput = /** @type {HTMLInputElement | undefined} */ (this.refs.emailInput);
    const errorElement = /** @type {HTMLElement | undefined} */ (this.refs.errorMessage);
    if (!emailInput || !errorElement) return;
    const email = emailInput.value.trim();
    if (!email) {
      showFieldError(emailInput, errorElement, false);
    } else if (emailInput.classList.contains('error')) {
      this.validateEmail(false);
    }
  };

  initValidation() {
    const emailInput = /** @type {HTMLInputElement | undefined} */ (this.refs.emailInput);
    const errorElement = /** @type {HTMLElement | undefined} */ (this.refs.errorMessage);
    if (!emailInput || !errorElement) return;

    emailInput.addEventListener('blur', this.#handleEmailBlur);
    emailInput.addEventListener('input', this.#handleEmailInput);
  }

  /** @param {SubmitEvent} event */
  handleSubmit = async (event) => {
    event.preventDefault();

    const form = /** @type {HTMLFormElement | undefined} */ (this.refs.bisForm);
    const emailInput = /** @type {HTMLInputElement | undefined} */ (this.refs.emailInput);
    const submitButton = /** @type {HTMLButtonElement | undefined} */ (this.refs.submitButton);
    const formContent = /** @type {HTMLElement | null} */ (this.querySelector('.js-bis-form-content'));
    const successMessage = /** @type {HTMLElement | undefined} */ (this.refs.successMessage);

    if (!form || !emailInput || !submitButton) return;
    if (this.isSubmitting) return;
    if (!this.validateEmail(true)) return;

    const email = emailInput.value.trim().toLowerCase();
    const variantId = form.dataset.variantId;
    const companyId = Theme?.settings?.klaviyo_company_id || '';

    if (!companyId) {
      this.showError(this.dataset.configError || 'Back in stock alerts are not configured.');
      return;
    }

    this.isSubmitting = true;
    const buttonText = submitButton.querySelector('.button-text');
    const buttonLoading = submitButton.querySelector('.button-loading');
    if (buttonText) buttonText.classList.add('hidden');
    if (buttonLoading) buttonLoading.classList.remove('hidden');
    submitButton.disabled = true;

    const variantIdComposite = variantId ? `$shopify:::$default:::${variantId}` : '';

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: variantIdComposite,
            },
          },
        },
      },
    };

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-06-15',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        form.reset();
        if (formContent && successMessage) {
          await this.showSuccess(formContent, successMessage);
        }
      } else {
        let msg = this.dataset.subscriptionFailed || 'Subscription failed';
        try {
          const errorData = await response.json();
          if (errorData?.errors?.length) {
            msg = errorData.errors[0].detail || errorData.errors[0].title || msg;
          }
        } catch (parseErr) {
          console.error('Error parsing JSON response:', parseErr);
        }
        this.showError(msg);
      }
    } catch (err) {
      console.error('Error during subscription:', err);
      this.showError(this.dataset.networkError || 'Network error. Please try again.');
    } finally {
      this.isSubmitting = false;
      if (buttonText) buttonText.classList.remove('hidden');
      if (buttonLoading) buttonLoading.classList.add('hidden');
      submitButton.disabled = false;
    }
  };

  /** @param {HTMLElement} formContent @param {HTMLElement} successMessage */
  showSuccess(formContent, successMessage) {
    formContent.classList.add('hidden');
    successMessage.classList.remove('hidden');
  }

  /** @param {string} message */
  showError(message) {
    const emailInput = /** @type {HTMLInputElement | undefined} */ (this.refs.emailInput);
    const errorMessage = /** @type {HTMLElement | undefined} */ (this.refs.errorMessage);
    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.classList.remove('hidden');
    }
    if (emailInput && errorMessage) showFieldError(emailInput, errorMessage, true);
  }
}

if (!customElements.get('back-in-stock-modal')) {
  customElements.define('back-in-stock-modal', BackInStockModal);
}

/** Restore body scroll when dialog open fails (prevents page freeze) */
function restoreBodyScroll() {
  const scrollY = document.body.style.top;
  document.body.style.width = '';
  document.body.style.position = '';
  document.body.style.top = '';
  if (scrollY) {
    window.scrollTo({ top: Number.parseInt(scrollY, 10) * -1, behavior: 'instant' });
  }
}

/** Open BIS modal when landing from quick-add with ?bis=1 */
function checkAndOpenBISModal() {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('bis') !== '1') return;

  const tryOpenModal = () => {
    const buyButtonsBlocks = document.querySelectorAll('.buy-buttons-block');
    for (const buyButtonsBlock of buyButtonsBlocks) {
      const bisModal = /** @type {BackInStockModal | null} */ (
        buyButtonsBlock.querySelector('back-in-stock-modal')
      );
      if (bisModal && typeof bisModal.showDialog === 'function') {
        try {
          bisModal.showDialog();
          const dialog = bisModal.refs?.dialog;
          if (!dialog?.open) {
            restoreBodyScroll();
            return false;
          }
        } catch (err) {
          console.warn('BIS modal open failed:', err);
          restoreBodyScroll();
          return false;
        }
        const url = new URL(window.location.href);
        url.searchParams.delete('bis');
        window.history.replaceState({}, '', url.toString());
        return true;
      }
    }
    return false;
  };

  const runCheck = () => {
    if (tryOpenModal()) return;

    requestAnimationFrame(() => {
      if (tryOpenModal()) return;

      const observer = new MutationObserver(() => {
        if (tryOpenModal()) {
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      const maxFrames = 300;
      let frames = 0;
      const disconnectAfterFrames = () => {
        frames++;
        if (frames >= maxFrames) {
          observer.disconnect();
        } else {
          requestAnimationFrame(disconnectAfterFrames);
        }
      };
      requestAnimationFrame(disconnectAfterFrames);
    });
  };

  requestAnimationFrame(runCheck);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', checkAndOpenBISModal);
} else {
  checkAndOpenBISModal();
}
